import React, { useState, useEffect } from "react";
import {
  Button,
  Col,
  Dropdown,
  OverlayTrigger,
  Row,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

import {
  setDropdownPreferences,
  setIsEnvPreferencesSaved,
  fetchStatus,
} from "../store/slice/preferenceSlice";
import {
  fetchEnvironment,
  fetchCompanies,
} from "../store/slice/preferenceSlice";
import { getUrl } from "../helpers/url";

import CmsEditBanner from "../helpers/cms/CmsEditBanner";
import { cmsConstructor } from "../helpers/cms/cmsConstructor";
import { useCmsHook } from "../helpers/cms/cmsHook";

function EnvironmentSelection() {
  const { setCmsInstance, cmsInstance } = useCmsHook();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  // preference states
  const {
    environmentPreference,
    companyPreference,
    environment: { loading: environmentLoading },
    environment: {
      environmentDetails: { environment },
    },
    company: {
      loading: companiesLoading,
      companyDetails: { companies },
    },
  } = useSelector((state) => state.preference);

  /**
   * @description This function is responsible for showing selected text in the dropdown button
   */
  const toggleText = ({ dropdownFor }) => {
    if (dropdownFor === "environment") {
      if (!environmentPreference) {
        return "Select environment";
      } else {
        const field = environment.find(
          (fieldItem) => fieldItem.name === environmentPreference
        );
        if (field) {
          return field.name;
        } else {
          return "selected environment deleted";
        }
      }
    }
    if (dropdownFor === "company") {
      if (!companyPreference) {
        return "select company";
      } else {
        const field = companies.find(
          (company) => company.id === companyPreference
        );
        if (field) {
          return field.name;
        } else {
          return "selected company deleted";
        }
      }
    }
  };

  /**
   * This function is responsible for
   */
  const onSavePreferences = async () => {
    try {
      setIsLoading(true);
      const account = searchParams.get("account");
      console.log(account);
      await axios.post(getUrl("REACT_APP_SAVE_ENV"), {
        environment: environmentPreference,
        account,
        companyId: companyPreference,
      });
      dispatch(setIsEnvPreferencesSaved());
      toast.success(
        cmsInstance &&
          cmsInstance.sendData().cmsContent.notifications.preference
            .successContent.preferencesSaved.message
      );
    } catch (error) {
      console.log(error);
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const account = searchParams.get("account");
    if (account) {
      dispatch(fetchStatus(account));
      dispatch(fetchEnvironment(account));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const account = searchParams.get("account");
    if (account && environmentPreference) {
      dispatch(fetchCompanies({ account, environmentPreference }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environmentPreference]);

  useEffect(() => {
    if (window.location.pathname === "/env") {
      const fetchCmsInstance = async () => {
        const cmsObj = await cmsConstructor();
        setCmsInstance(cmsObj);
      };
      fetchCmsInstance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page-bg-container w-100">
      {window.location.pathname === "/env" && (
        <div className="d-flex flex-row justify-content-center">
          <CmsEditBanner />
        </div>
      )}
      <div className="page-bg-container d-flex flex-row justify-content-center align-items-center">
        <Row className="d-flex flex-row justify-content-center align-items-center h-100 w-100 px-2">
          <Col sm={10} md={5} lg={3} className="env-card px-0">
            <div>
              <div className="oauth-card-heading-container">
                {cmsInstance &&
                  cmsInstance.showRichText(
                    "cmsContent.preference.heading",
                    "oauth-card-heading my-0"
                  )}
              </div>

              {environmentLoading ? (
                <div className="d-flex flex-row justify-content-center align-items-center my-3">
                  <Spinner
                    animation="border"
                    variant="secondary"
                    role="status"
                    size="md"
                  ></Spinner>
                </div>
              ) : (
                <div className="px-4 my-4">
                  {cmsInstance &&
                    cmsInstance.showRichText(
                      "cmsContent.preference.description",
                      "oauth-card-description"
                    )}

                  {environment && (
                    <Col>
                      <span className="rules-sub-headings mb-2">
                        Select Environment
                      </span>

                      <Dropdown>
                        <Dropdown.Toggle className="setup-fields-dropdown">
                          {toggleText({ dropdownFor: "environment" })}
                        </Dropdown.Toggle>

                        <Dropdown.Menu
                          renderOnMount={true}
                          popperConfig={{ strategy: "fixed" }}
                          className="setup-fields-dropdown-items-container"
                        >
                          {environment &&
                            environment.length > 0 &&
                            environment.map((env) => (
                              <Dropdown.Item
                                key={env.type}
                                className="px-3 dropdown-item"
                                onClick={() =>
                                  dispatch(
                                    setDropdownPreferences({
                                      dropdownFor: "environment",
                                      selectedOption: env.name,
                                    })
                                  )
                                }
                              >
                                {env.name}
                              </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </Col>
                  )}
                  {companiesLoading ? (
                    <div className="d-flex flex-row justify-content-center align-items-center my-3">
                      <Spinner
                        animation="border"
                        variant="secondary"
                        role="status"
                        size="md"
                      ></Spinner>
                    </div>
                  ) : (
                    <>
                      {environmentPreference && companies && (
                        <Col className="mt-3">
                          <span className="rules-sub-headings mb-2">
                            Select Company
                          </span>
                          <Dropdown>
                            <Dropdown.Toggle className="setup-fields-dropdown">
                              {toggleText({ dropdownFor: "company" })}
                            </Dropdown.Toggle>

                            <Dropdown.Menu
                              renderOnMount={true}
                              popperConfig={{ strategy: "fixed" }}
                              className="setup-fields-dropdown-items-container"
                            >
                              {companies &&
                                companies.length > 0 &&
                                companies.map((company) => (
                                  <Dropdown.Item
                                    key={company.id}
                                    className="px-3 dropdown-item"
                                    onClick={() =>
                                      dispatch(
                                        setDropdownPreferences({
                                          dropdownFor: "company",
                                          selectedOption: company.id,
                                        })
                                      )
                                    }
                                  >
                                    {company.name}
                                  </Dropdown.Item>
                                ))}
                            </Dropdown.Menu>
                          </Dropdown>
                        </Col>
                      )}
                    </>
                  )}

                  <div className="d-flex flex-row justify-content-end mt-3">
                    {!companyPreference || !environmentPreference ? (
                      <OverlayTrigger
                        placement="right"
                        overlay={
                          <Tooltip style={{ fontSize: "11px" }}>
                            {showTooltipText(
                              companyPreference,
                              environmentPreference
                            )}
                          </Tooltip>
                        }
                      >
                        <Button
                          className="save-changes-button"
                          style={{ opacity: "0.9", cursor: "not-allowed" }}
                        >
                          Save
                        </Button>
                      </OverlayTrigger>
                    ) : (
                      <Button
                        className="save-changes-button"
                        onClick={() => onSavePreferences()}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : (
                          <span>Save</span>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default EnvironmentSelection;

/**
 * This function is responsible for showing the custom message in the tooltip
 * @returns custom message
 */
function showTooltipText(companyPreference, environmentPreference) {
  if (!environmentPreference) {
    return "Please select environment";
  }
  if (!companyPreference) {
    return "Please select company";
  }
}
